import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useBetaData } from '../../../contexts/BetaDataContext';
import { createPhase, updatePhase, deletePhase } from '../../../api/phases';
import { useToast } from '../../common/Toast';
import './PhaseCreateModal.scss';

const PhaseCreateModal = ({ isOpen, onClose, onSuccess, phase = null, projectId }) => {
  const { profile, viewingCompanyId } = useAuth();
  const { refreshProjectPhases } = useBetaData();
  const toast = useToast();
  const isEdit = !!phase;

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [dateError, setDateError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (phase) {
        setName(phase.name || '');
        setStartDate(phase.start_date || '');
        setEndDate(phase.end_date || '');
      } else {
        setName('');
        setStartDate('');
        setEndDate('');
      }
      setDateError('');
      setShowDeleteConfirm(false);
    }
  }, [isOpen, phase]);

  if (!isOpen) return null;

  const handleStartDateChange = (val) => {
    setStartDate(val);
    if (endDate && val && endDate < val) {
      setDateError('結束日期不可早於開始日期');
    } else {
      setDateError('');
    }
  };

  const handleEndDateChange = (val) => {
    setEndDate(val);
    if (startDate && val && val < startDate) {
      setDateError('結束日期不可早於開始日期');
    } else {
      setDateError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || dateError) return;

    setSubmitting(true);

    const payload = {
      name: name.trim(),
      start_date: startDate || null,
      end_date: endDate || null,
    };

    let result;
    if (isEdit) {
      result = await updatePhase(phase.id, payload);
    } else {
      result = await createPhase({
        ...payload,
        project_id: projectId,
        company_id: viewingCompanyId || profile.company_id,
      });
    }

    setSubmitting(false);

    if (!result.error) {
      await refreshProjectPhases(projectId);
      onSuccess?.(result.data || phase);
      onClose();
    } else {
      toast.error(isEdit ? '更新階段失敗' : '建立階段失敗');
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    const { error } = await deletePhase(phase.id);
    setSubmitting(false);

    if (error) {
      toast.error('刪除階段失敗');
    } else {
      toast.success('階段已刪除');
      await refreshProjectPhases(projectId);
      onSuccess?.(null);
      onClose();
    }
  };

  return (
    <div className="phase-create-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="phase-create-modal">
        <div className="modal-header">
          <h3>{isEdit ? '編輯階段' : '新增階段'}</h3>
          <button type="button" className="close-btn" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4l10 10M14 4L4 14" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-group">
            <label>階段名稱 *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例如：需求分析、UI 設計、開發..."
              autoFocus
            />
          </div>

          <div className="form-row">
            <div className="form-group half">
              <label>開始日期</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleStartDateChange(e.target.value)}
              />
            </div>
            <div className="form-group half">
              <label>結束日期</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => handleEndDateChange(e.target.value)}
              />
            </div>
          </div>
          {dateError && <div className="date-error">{dateError}</div>}

          <div className="modal-actions">
            {isEdit && (
              <button
                type="button"
                className="btn-delete"
                onClick={() => setShowDeleteConfirm(true)}
              >
                刪除階段
              </button>
            )}
            <div className="actions-right">
              <button type="button" className="btn-cancel" onClick={onClose}>
                取消
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={!name.trim() || !!dateError || submitting}
              >
                {submitting ? (isEdit ? '儲存中...' : '建立中...') : (isEdit ? '儲存' : '新增階段')}
              </button>
            </div>
          </div>
        </form>

        {showDeleteConfirm && (
          <div className="delete-confirm-overlay" onClick={(e) => e.target === e.currentTarget && setShowDeleteConfirm(false)}>
            <div className="delete-confirm-dialog">
              <p className="delete-confirm-title">確定要刪除「{phase?.name}」嗎？</p>
              <p className="delete-confirm-desc">此操作無法復原，階段底下的所有交付項目也會一併刪除。</p>
              <div className="delete-confirm-actions">
                <button className="btn-cancel" onClick={() => setShowDeleteConfirm(false)}>
                  取消
                </button>
                <button className="btn-danger" onClick={handleDelete} disabled={submitting}>
                  {submitting ? '刪除中...' : '確定刪除'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhaseCreateModal;
